<template>
<div>
    <CCol sm="12">
    <CCard>
        <CCardBody class="d-flex flex-column">
          <!-- Title -->
          <p v-if="basePoi"><b>{{ $t('poiIntro') }}</b> {{basePoi.names[currentLanguage.key]}}</p>
          <!-- Language dropdown -->
          <LanguageDropdown class="align-self-end" v-model="currentLanguage"></LanguageDropdown>
          <div class="col-12 d-flex flex-row flex-wrap justify-content-between p-0 mt-4 mb-4">
            <div class="col-12 col-lg-6 d-flex flex-column justify-content-between">
              <CRow>
                <!-- Image -->
                <ImageCrop class="image-crop col-12 col-lg-5" ref="imageCrop" v-model="imageData" @fileChanged="fileChanged" v-bind:class="{'is-invalid': isImageValid == false }" :ratio="16/9" ratioDisplay="16/9"></ImageCrop>
                <div class="col-12 col-lg-7 d-flex flex-column justify-content-between">
                  <!-- Name -->
                  <div class="autocomplete">
                    <CInput :label="$t('nameMandatory')" placeholder="" v-model="poi.names[currentLanguage.key]" :is-valid="nameValidator() && errorValidator('Names')" :invalid-feedback="errorOutput['Names']"/>
                  </div>
                  <!-- Categories -->
                  <div role="group" class="form-group">
                    <label class="">{{ $t('categoriesMandatory') }}</label>
                    <OptionsDropdown class="align-self-end" v-model="poi.categories" :options="categories" :title="$t('selectCategories')" :is-valid="categoryValidator && errorValidator('Categories')" :invalid-feedback="errorOutput['Categories']" :index="1"></OptionsDropdown>
                  </div>
                </div>
              </CRow>
              <CRow>
                <!-- Latitude -->
                <CCol sm="6">
                  <CInput type="number" :label="$t('latitudeMandatory')" placeholder="" v-model="poi.latitude" :is-valid="numberValidator(poi.latitude) && errorValidator('Latitude')" :invalid-feedback="errorOutput['Latitude']"/>
                </CCol>
                <!-- Longitude -->
                <CCol sm="6">
                  <CInput type="number" :label="$t('longitudeMandatory')" placeholder="" v-model="poi.longitude" :is-valid="numberValidator(poi.longitude) && errorValidator('Longitude')" :invalid-feedback="errorOutput['Longitude']"/>
                </CCol>
                <!-- Location -->
                <CCol v-if="basePoi" sm="6">
                  <p>{{ $t('location') }}:</p><p>{{poi.address}}</p>
                </CCol>
              </CRow>
            </div>
            <!-- Map -->
            <CRow class="col-12 col-lg-6 pr-lg-4">
              <GmapMap :center="center" :zoom="zoom" style="width: 100%; height: 300px;">
                <GmapMarker v-if="poiPosition" :position="poiPosition" :draggable="true" @dragend="getMarkerPosition($event.latLng)"/>
              </GmapMap>
            </CRow>
          </div>
          <!-- Description -->
          <div class="col-12 d-flex flex-column">
            <label class=""> {{ $t('descriptionMandatory') }}</label>
            <textarea v-model="poi.descriptions[currentLanguage.key]" v-bind:class="{'is-invalid': descriptionValidator() == false || errorValidator('Descriptions') == false }" cols="30" rows="10"></textarea>
            <!-- <vue-editor v-model="poi.descriptions[currentLanguage.key]" v-bind:class="{'is-invalid': descriptionValidator() == false || errorValidator('Descriptions') == false }"></vue-editor> -->
            <div v-if="errorOutput['Descriptions']" class="invalid-feedback">{{errorOutput['Descriptions']}}</div>
          </div>
          <!-- Action button + loading -->
          <CSpinner v-if="loading" variant="success" small class="align-self-end m-3"></CSpinner>
          <CButton v-else type="button" class="btn btn-primary align-self-end m-3" @click="submitClicked">{{ $t('acceptChanges') }}</CButton>
        </CCardBody>
      </CCard>
    </CCol>
    <ConfirmationPopUp :confirmationMessage="confirmationMessage" @closeConfirmRequested="onClickConfirmationPopup" v-model="showConfirmationPopup"></ConfirmationPopUp>
  </div>
</template>
 
<script>
import { deepClone } from "@/helpers/utils.js";
import PointOfInterest from "@/classes/PointOfInterest";
import { mapState, mapActions } from 'vuex'
import InputPointOfInterest from "@/classes/InputPointOfInterest";
import InputGetList from "@/classes/InputGetList";
import LanguageDropdown from '../controls/LanguageDropdown.vue'
import OptionsDropdown from '../controls/OptionsDropdown.vue'
import Autocomplete from "../controls/AutocompletePoi.vue";
import ImageCrop from "../controls/ImageCrop.vue";
import ConfirmationPopUp from "@/views/controls/popups/ConfirmationPopUp.vue";
import { CONSTANTS } from '@/helpers/consts';
import router from '@/router/index';
import { VueEditor } from "vue2-editor";

const CATEGORY_TYPE = "Poi";

  export default {
  components: { LanguageDropdown, OptionsDropdown, ConfirmationPopUp, Autocomplete, ImageCrop, VueEditor },
  data () {
    return {
      imageData: '', // The direct binding to poi.path was not working correctly
      selectedFile: '',
      submitted: false,
      currentLanguage: CONSTANTS.supportedLanguages[0],
      poi: new PointOfInterest({}, {}, []),
      loading: false,
      hasError: false,
      showErrorMessages: false,
      showConfirmationPopup: false,
      confirmationMessage:"",
    }
  },
  props:{
    basePoi:
    {
      type: Object,
      default: null,
    },
  },
  computed:{
    ...mapState({ 
      alertType: state => state.alert.type,
      alertOutput: state => state.alert.output
    }),
    errorOutput: function()
    {
      var result = this.alertOutput && Array.isArray(this.alertOutput) ? this.alertOutput : [];

      if(this.submitted)
      {
        // Validate name
        for (const [key, value] of Object.entries(this.poi.names)) {
            if(!value)
            {
                result['Names'] = this.$i18n.t("validateBothTranslations");
                break;
            };
        }

        // Validate description
        for (const [key, value] of Object.entries(this.poi.descriptions)) {
            if(!value)
            {
                result['Descriptions'] = this.$i18n.t("validateBothTranslations");
                break;
            };
        }
      }

      return result;
    },
    ...mapState('categories', ['listOutput']),
    ...mapState('pois', ['processTask']),
    ...mapState('pois', ['coordinatesOutput']),
    categoriesInfo: function() {
      return this.listOutput && this.listOutput.data ? this.listOutput.data : null;
    },
    categories: function()
    {
      var result = [];

      if(this.categoriesInfo)
      {
          this.categoriesInfo.forEach(category => 
          {
            result.push({key: category.id, label: category.name });
          });
      }

      return result;
    },
    isImageValid: function(){
      return this.imageValidator() && this.errorValidator('image');
    },
    poiPosition: function(){
      return this.poi && this.poi.latitude && this.poi.longitude ? { lat: parseFloat(this.poi.latitude), lng: parseFloat(this.poi.longitude) } : null;
    },
    // In case the position is not defined, show the Portugal map
    center: function(){
      return this.poiPosition ? this.poiPosition : {lat: 39.9838329, lng: -6.09731 };
    },
    zoom: function() {
      return this.poiPosition ? 10 : 5;
    },
    poiId: function()
    {
      return this.poi.id;
    },
    poiStatus: function()
    {
      return this.poi.status;
    }
  },
  mounted: function()
  {
    // Set POI object
    this.resetPOI();

    // Get categories
    var input = new InputGetList(999, 1, "", "Name", "id");
    input.type = CATEGORY_TYPE;

    this.getList(input); 
  },    
  destroyed: function()
  {
    this.reset();
  },
  methods:{    
    ...mapActions('categories', ['getList']),
    ...mapActions('categories', ['reset']),
    ...mapActions('pois', ['getCoordinates']),
    onPlaceSelected(place){
      this.getCoordinates(place.place_id);
    },
    chooseImage () {
      this.$refs.fileInput.click();
    },   
    resetPOI(){
      if(!this.basePoi)
      {
        // Initiliaze new poi
        this.poi = new PointOfInterest({}, {}, []);
        CONSTANTS.supportedLanguages.forEach(language => 
        {
          this.poi.names[language.key] = ""; 
          this.poi.descriptions[language.key] = ""; 
        });
      }
      else
      {
        this.poi = deepClone(new PointOfInterest, this.basePoi);  
        this.poi.categories = this.poi.categories.map(x=> x.id);
        this.imageData = this.poi.photoPath;
      }
    },
    fileChanged(val)
    {
      this.selectedFile = val;
    },
    submitClicked(){
      this.submitted = true;

      // Emit event
      if(this.poi.isValid() && (this.selectedFile || this.basePoi))
      {
        this.loading = true;
        var id = this.basePoi && this.$route.params.id ? this.$route.params.id : null;

        var input = new InputPointOfInterest(id, this.poi, this.selectedFile);
        this.$emit('submit-requested', input);
      }
    },
    onClickConfirmationPopup(value){
        this.showConfirmationPopup = value;     
        router.push('/pois/list');
    },
    nameValidator(){
        var result = undefined;

        if(this.submitted)
        {
            // Validate all fields
            for (const [key, value] of Object.entries(this.poi.names)) {
                if(!value)
                {
                    result = false;
                    break;
                };
            }
            
            if(result == undefined)
            {
                result = true;
            }
        }

        return result;
    },
    descriptionValidator(){
        var result = undefined;

        if(this.submitted)
        {
            // Validate all fields
            for (const [key, value] of Object.entries(this.poi.descriptions)) {
                if(!value)
                {
                    result = false;
                    break;
                };
            }
            
            if(result == undefined)
            {
                result = true;
            }
        }

        return result;
    },
    categoryValidator(){
      var result = undefined;

      if(this.submitted && (!Array.isArray(this.poi.categories) || !this.poi.categories.length))
      {
        result = false;
      }

      return result;
    },
    imageValidator(){
      var result = undefined;

      if(this.submitted && !this.imageData)
      {
        result = false;
      }

      return result;
    },
    numberValidator(number){
      var result = undefined;

      if(this.submitted && !number)
      {
        result = false;
      }

      return result;
    },
    errorValidator(fieldName)
    {
        var result = undefined;

        if(this.submitted && Object.keys(this.errorOutput).length)
        {
          result = !Object.keys(this.errorOutput).includes(fieldName);
        }

        return result;
    },
    isCategoryActive(categoryId)
    {
      return this.poi && this.poi.categories ? this.poi.categories.includes(categoryId) : false;
    },
    getMarkerPosition(val){
      this.poi.latitude = val.lat();
      this.poi.longitude = val.lng();
    }
  },
  watch:
  {    
    processTask: function(val)
    {
      if(val)
      {
        this.showConfirmationPopup = true;
      }
    },
    basePoi: function()
    {
      this.resetPOI();
    },
    alertType: function()
    {
        this.loading = false;
    },
    alertOutput: function(val)
    {
      this.loading = false;

      // Show pop up in case it is a string error
      // In case it is an array, it is shown on the field.
      // An exception to this, due to the look of the input, is the image.
      if(typeof this.alertOutput === 'string' || this.alertOutput instanceof String || this.errorOutput['Image'])
      {
        var msg = this.alertOutput;
        if(this.errorOutput['Image'])
        {
          msg = this.$i18n.t("Image") + ": " + this.errorOutput['Image'];
        }

        alert(msg);
      }
    },
    coordinatesOutput: function(val)
    {
      if(val && val.object)
      {
        this.poi.latitude = val.object.lat;
        this.poi.longitude = val.object.lng;
      }
    }
  }
}
</script> 
 
<style scoped>
.base-image-input {
  display: block;
  width: 200px;
  height: 200px;
  cursor: pointer;
  background-size: cover;
  background-position: center center;
}

.placeholder {
  background: #F0F0F0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #333;
  font-size: 18px;
  font-family: Helvetica;
}

.placeholder:hover {
  background: #E0E0E0;
}

.file-input {
  display: none;
}

.input-group-append
{
  height: fit-content;
}

img
{
  width: 50%;
  height: 50%;
}

.image-crop
{
  display: flex;
  justify-content: center;
}

@media (min-width: 992px) { 
 .image-crop
  {
    display: flex;
    justify-content: flex-start;
  }
}



</style> 